import Keycloak from "keycloak-js";

const _kc = new Keycloak("/keycloak.json");
let refreshTimer;
let refreshInterval = 60 * 1000; // 1 minute
let refreshFailureCount = 0;
const maxRefreshFailuresBeforeLogout = 10;

const clearClientSession = () => {
  try {
    sessionStorage.removeItem("token");
  } catch (e) {
    console.log("sessionStorage clear error:", e);
  }
};


/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
  _kc
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
      pkceMethod: "S256",
    })
    .then((authenticated) => {
      if (!authenticated) {
        console.log("user is not authenticated..!");
      }
      onAuthenticatedCallback();

      // Periodic token refresh if user is active
      refreshTimer = setInterval(() => {
        _kc
          .updateToken(-1) // force refresh every interval regardless of remaining lifetime
          .then((refreshed) => {
            refreshFailureCount = 0;
            if (refreshed) {
              console.log("Token was refreshed successfully");
            } else {
              console.log("Token still valid, no refresh needed");
            }
          })
          .catch(() => {
            // Retry with a forced refresh before deciding to end session.
            _kc
              .updateToken(0)
              .then(() => {
                refreshFailureCount = 0;
                console.log("Token refresh recovered on forced retry");
              })
              .catch(() => {
                refreshFailureCount += 1;
                if (refreshFailureCount >= maxRefreshFailuresBeforeLogout) {
                  console.log("Token refresh failed repeatedly → logging out");
                  doLogout();
                } else {
                  console.log(
                    `Token refresh failed (${refreshFailureCount}/${maxRefreshFailuresBeforeLogout}), will retry`
                  );
                }
              });
          });
      }, refreshInterval);
    })
    .catch(console.error);

  _kc.onAuthLogout = () => {
    console.log("Keycloak triggered logout");
    clearClientSession();
    cleanupAfterLogout();
  };
};

const cleanupAfterLogout = () => {
  clearInterval(refreshTimer);
};

const doLogin = _kc.login;

const doLogout = () => {
  refreshFailureCount = 0;
  clearClientSession();
  cleanupAfterLogout();
  _kc.logout();
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
  _kc.updateToken(5).then(successCallback).catch(doLogin);

/**
 * Silently refreshes the Keycloak access token if it expires within 30s.
 * Resolves with the fresh token string.
 * Rejects (does NOT redirect) if the refresh token is also expired — caller
 * should treat a rejection as "session fully expired → doLogout()".
 */
const ensureFreshToken = () =>
  _kc.updateToken(30).then(() => _kc.token);

const getUsername = () => _kc.tokenParsed?.preferred_username;
const getWelcomeName = () => _kc.tokenParsed?.given_name;
const getEmail = () => _kc.tokenParsed?.email;
const getUserId = () => _kc.tokenParsed?.sub;

const hasRole = (roles) => roles.some((role) => _kc.hasRealmRole(role));

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  ensureFreshToken,
  getUsername,
  getWelcomeName,
  getEmail,
  getUserId,
  hasRole,
};

export default UserService;
